import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ChatMessage, ChatStreamEvent } from 'src/app/shared/models/chat-message';
import { ChatConversation } from 'src/app/shared/models/chat-conversation';
import { ChatService } from 'src/app/shared/services/chat.service';
import { AppHttpService } from 'src/app/shared/services/app-http.service';
import { CarouselComponent } from 'src/app/shared/components/carousel/carousel.component';

@Component({
  selector: 'app-chat-panel',
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss'],
  standalone: false,
})
export class ChatPanelComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageContainer') messageContainer: ElementRef;

  messages: ChatMessage[] = [];
  currentConversationId: string = null;
  projectUuid: string = null;
  isFavorite = false;
  favoriteConversations: ChatConversation[] = [];
  availableProjects: any[] = [];
  needsProjectSelection = false;
  projectResolved = false;
  isLoading = false;
  isRecording = false;
  isFullscreen = false;
  streamingText = '';
  inputText = '';
  shouldScrollToBottom = false;

  // Tab system state
  activeTab: 'new' | 'history' = 'new';
  historyFilter: 'all' | 'favorites' = 'all';
  conversations: ChatConversation[] = [];
  conversationsLoaded = false;

  // Consent state
  consentChecked = false;
  consentAccepted = false;
  consentLoading = false;

  // Rate limit state
  rateLimitMessage: string = null;
  downloadingPdf: string = null;

  suggestedQuestions: string[] = [
    'Open issues?',
    'Failed tests?',
    'Show me work completed this month',
    'Pending reports?',
    'Open RFIs?',
    'Summarize photos by inspector and month',
  ];

  private streamSubscription: Subscription;
  private recognition: any = null;

  constructor(
    public dialogRef: MatDialogRef<ChatPanelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { projectUuid: string },
    private chatService: ChatService,
    private http: AppHttpService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // console.log('[CHAT PANEL] data =', JSON.stringify(this.data));

    // Check AI consent first
    this.chatService.getConsent().subscribe({
      next: (resp: any) => {
        this.consentAccepted = resp?.data?.accepted || false;
        this.consentChecked = true;
      },
      error: () => {
        this.consentChecked = true;
        this.consentAccepted = false;
      },
    });

    if (this.data?.projectUuid) {
      this.projectUuid = this.data.projectUuid;
      this.projectResolved = true;
      this.loadConversations();
      this.loadFavorites();
    } else {
      // console.log('[CHAT PANEL] No projectUuid, resolving...');
      // No project context -- fetch projects and let user pick
      this.resolveProject();
    }
  }

  private resolveProject(): void {
    this.http.getService('/v1/project').subscribe({
      next: (resp: any) => {
        // console.log('[CHAT PANEL] /v1/project response:', JSON.stringify(resp)?.substring(0, 500));
        const projects = resp?.data || [];
        // console.log('[CHAT PANEL] projects count:', projects.length, 'first:', projects[0]?.uuid, projects[0]?.name);
        if (projects.length === 1) {
          this.projectUuid = projects[0].uuid || projects[0].id;
          this.projectResolved = true;
          this.loadConversations();
          this.loadFavorites();
        } else if (projects.length > 1) {
          this.availableProjects = projects;
          this.needsProjectSelection = true;
          this.projectResolved = true;
        }
      },
      error: (err: any) => {
        console.error('[CHAT PANEL] Failed to load projects', err);
        this.projectResolved = true;
      },
    });
  }

  acceptConsent(): void {
    this.consentLoading = true;
    this.chatService.acceptConsent().subscribe({
      next: () => {
        this.consentAccepted = true;
        this.consentLoading = false;
      },
      error: (err: any) => {
        console.error('Failed to accept consent', err);
        this.consentLoading = false;
      },
    });
  }

  selectProject(project: any): void {
    this.projectUuid = project.uuid || project.id;
    this.needsProjectSelection = false;
    this.availableProjects = [];
    this.loadConversations();
    this.loadFavorites();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
    }
    if (this.recognition) {
      this.recognition.abort();
    }
  }

  loadConversations(): void {
    if (!this.projectUuid) {
      console.warn('[CHAT PANEL] loadConversations called with no projectUuid');
      return;
    }
    // Don't auto-load previous conversations -- always start fresh
    // User can access past conversations via favorites
  }

  loadMessages(conversationId: string): void {
    this.currentConversationId = conversationId;
    this.isFavorite = this.favoriteConversations.some(c => c.uuid === conversationId);
    this.chatService.getMessages(this.projectUuid, conversationId).subscribe({
      next: (resp: any) => {
        this.messages = resp?.data || [];
        this.shouldScrollToBottom = true;
      },
      error: (err: any) => {
        console.error('Failed to load messages', err);
      },
    });
  }

  sendMessage(): void {
    const question = this.inputText?.trim();
    if (!question || this.isLoading || !this.projectUuid) {
      return;
    }

    // Add user message to the list
    const userMessage: ChatMessage = {
      uuid: null,
      conversation_id: null,
      role: 'user',
      content: question,
      structured_data: null,
      tool_calls: null,
      tokens_used: null,
      created_at: new Date().toISOString(),
    };
    this.messages.push(userMessage);
    this.inputText = '';
    this.isLoading = true;
    this.streamingText = '';
    this.shouldScrollToBottom = true;

    // Clean up any previous subscription
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
    }

    this.streamSubscription = this.chatService
      .sendMessage(this.projectUuid, question, this.currentConversationId)
      .subscribe({
        next: (event: ChatStreamEvent) => {
          this.handleStreamEvent(event);
        },
        error: (err: any) => {
          console.error('Stream error', err);
          this.isLoading = false;
          this.streamingText = '';

          let errorContent = 'Sorry, something went wrong. Please try again.';

          if (err?.code === 'AI_CONSENT_REQUIRED') {
            // Consent was revoked or not given — show consent banner
            this.consentAccepted = false;
            this.messages.pop();
            return;
          }

          if (err?.code === 'ORG_CHAT_DISABLED') {
            errorContent = 'The AI Assistant is not enabled for your organization. Please contact your administrator.';
          }

          if (err?.status === 429) {
            const retryAfter = err.retryAfter || 60;
            errorContent = `You're sending messages too quickly. Please wait ${retryAfter} seconds before trying again.`;
            this.rateLimitMessage = errorContent;
            setTimeout(() => { this.rateLimitMessage = null; }, retryAfter * 1000);
          }

          this.messages.push({
            uuid: null,
            conversation_id: null,
            role: 'assistant',
            content: errorContent,
            structured_data: null,
            tool_calls: null,
            tokens_used: null,
            created_at: new Date().toISOString(),
          });
          this.shouldScrollToBottom = true;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  private pendingStructuredData: any = null;

  private handleStreamEvent(event: ChatStreamEvent): void {
    switch (event.event) {
      case 'message_start':
        this.currentConversationId = event.data.conversation_id || this.currentConversationId;
        this.pendingStructuredData = null;
        break;

      case 'text_delta':
        this.streamingText += event.data.text || '';
        this.shouldScrollToBottom = true;
        break;

      case 'structured_data':
        // Store it to attach to the final message
        this.pendingStructuredData = event.data;
        break;

      case 'message_end':
        const assistantMessage: ChatMessage = {
          uuid: event.data.message_id || null,
          conversation_id: event.data.conversation_id || null,
          role: 'assistant',
          content: this.streamingText,
          structured_data: this.pendingStructuredData || event.data.structured_data || null,
          tool_calls: event.data.tool_calls || null,
          tokens_used: event.data.tokens_used || null,
          created_at: new Date().toISOString(),
        };
        this.messages.push(assistantMessage);
        this.streamingText = '';
        this.pendingStructuredData = null;
        this.isLoading = false;
        this.shouldScrollToBottom = true;
        break;

      case 'error':
        console.error('Stream event error', event.data);
        this.isLoading = false;
        this.streamingText = '';
        break;
    }
  }

  askQuestion(question: string): void {
    this.inputText = question;
    this.sendMessage();
  }

  loadFavorites(): void {
    if (!this.projectUuid) {
      return;
    }
    this.chatService.getFavorites(this.projectUuid).subscribe({
      next: (resp: any) => {
        this.favoriteConversations = resp?.data || [];
      },
      error: (err: any) => {
        console.error('Failed to load favorites', err);
      },
    });
  }

  loadConversation(conversationId: string): void {
    this.loadMessages(conversationId);
    // Check if this conversation is a favorite
    this.isFavorite = this.favoriteConversations.some(c => c.uuid === conversationId);
  }

  toggleFavorite(): void {
    if (!this.currentConversationId) return;

    this.chatService.toggleFavorite(this.projectUuid, this.currentConversationId).subscribe({
      next: (resp: any) => {
        this.isFavorite = resp?.data?.is_favorite || false;
        this.loadFavorites(); // Refresh favorites list
      },
      error: (err: any) => {
        console.error('Failed to toggle favorite', err);
      },
    });
  }

  newConversation(): void {
    const hadConversation = this.messages.length > 0;
    this.messages = [];
    this.currentConversationId = null;
    this.isFavorite = false;
    this.streamingText = '';
    this.isLoading = false;
    this.inputText = '';
    // If coming from an active conversation, show History tab so user can pick another
    if (hadConversation) {
      this.activeTab = 'history';
      this.loadAllConversations();
    } else {
      this.activeTab = 'new';
    }
  }

  switchToTab(tab: 'new' | 'history'): void {
    this.activeTab = tab;
    if (tab === 'history' && !this.conversationsLoaded) {
      this.loadAllConversations();
    }
  }

  switchToHistory(): void {
    this.activeTab = 'history';
    if (!this.conversationsLoaded) {
      this.loadAllConversations();
    }
  }

  loadAllConversations(): void {
    if (!this.projectUuid) {
      return;
    }
    this.chatService.getConversations(this.projectUuid).subscribe({
      next: (resp: any) => {
        this.conversations = resp?.data || [];
        this.conversationsLoaded = true;
      },
      error: (err: any) => {
        console.error('Failed to load conversations', err);
      },
    });
  }

  get filteredConversations(): ChatConversation[] {
    if (this.historyFilter === 'favorites') {
      return this.conversations.filter(c => c.is_favorite);
    }
    return this.conversations;
  }

  get firstUserQuestion(): string {
    const msg = this.messages.find(m => m.role === 'user');
    return msg ? msg.content.substring(0, 60) + (msg.content.length > 60 ? '...' : '') : '';
  }

  rerunQuery(): void {
    const firstQuestion = this.messages.find(m => m.role === 'user')?.content;
    if (!firstQuestion) return;
    // Start a new conversation with the same question
    this.messages = [];
    this.currentConversationId = null;
    this.inputText = firstQuestion;
    this.sendMessage();
  }

  get pinnedConversations(): ChatConversation[] {
    return this.conversations.filter(c => c.is_favorite);
  }

  get recentConversations(): ChatConversation[] {
    return this.conversations.filter(c => !c.is_favorite);
  }

  loadConversationFromHistory(conv: ChatConversation): void {
    this.loadConversation(conv.uuid);
  }

  toggleConversationFavorite(conv: ChatConversation, event: Event): void {
    event.stopPropagation();
    if (!this.projectUuid) return;

    this.chatService.toggleFavorite(this.projectUuid, conv.uuid).subscribe({
      next: (resp: any) => {
        conv.is_favorite = resp?.data?.is_favorite || false;
        this.loadFavorites();
      },
      error: (err: any) => {
        console.error('Failed to toggle favorite', err);
      },
    });
  }

  getRelativeDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.dialogRef.addPanelClass(this.isFullscreen ? 'chat-panel-dialog-fullscreen' : 'chat-panel-dialog');
    this.dialogRef.removePanelClass(this.isFullscreen ? 'chat-panel-dialog' : 'chat-panel-dialog-fullscreen');
    if (this.isFullscreen) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.updatePosition({ top: '0', left: '0', right: '' });
    } else {
      this.dialogRef.updateSize('480px', '100vh');
      this.dialogRef.updatePosition({ top: '0', right: '0', left: '' });
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
      // Reset textarea height after sending
      const textarea = event.target as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
      }
    }
  }

  autoGrow(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }

  toggleVoice(): void {
    if (this.isRecording) {
      this.stopVoice();
    } else {
      this.startVoice();
    }
  }

  private startVoice(): void {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser. Please use Chrome.');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    this.recognition.onstart = () => {
      this.isRecording = true;
    };

    this.recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      this.inputText = transcript;
    };

    this.recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      this.isRecording = false;
    };

    this.recognition.onend = () => {
      this.isRecording = false;
      // Auto-send if we got text
      if (this.inputText?.trim()) {
        this.sendMessage();
      }
    };

    this.recognition.start();
  }

  private stopVoice(): void {
    if (this.recognition) {
      this.recognition.stop();
    }
  }

  hasTable(msg: ChatMessage): boolean {
    // Check structured_data for table type
    if (msg.structured_data?.type === 'table' && msg.structured_data?.columns) return true;
    // Check if content has markdown tables (fallback)
    if (msg.content && /^\|.+\|$/m.test(msg.content)) return true;
    return false;
  }

  private getQuestionForFilename(msgIndex: number): string {
    // Find the user message preceding this assistant message
    for (let i = msgIndex - 1; i >= 0; i--) {
      if (this.messages[i]?.role === 'user') {
        return this.messages[i].content
          .substring(0, 50)
          .replace(/[^a-zA-Z0-9 ]/g, '')
          .trim()
          .replace(/\s+/g, '-')
          .toLowerCase();
      }
    }
    return 'response';
  }

  downloadCsv(msg: ChatMessage, msgIndex?: number): void {
    let csvContent = '';

    // Try structured_data table first
    if (msg.structured_data?.type === 'table' && msg.structured_data.columns) {
      const { columns, rows } = msg.structured_data;
      csvContent = columns.map((c: string) => `"${c.replace(/"/g, '""')}"`).join(',') + '\n';
      for (const row of rows) {
        csvContent += row.map((cell: any) => `"${String(cell || '').replace(/"/g, '""')}"`).join(',') + '\n';
      }
    } else if (msg.content) {
      // Parse markdown tables from content
      const lines = msg.content.split('\n');
      const tableLines = lines.filter((l: string) => l.includes('|') && (l.match(/\|/g) || []).length >= 2);
      const isSep = (l: string) => /^[\s|:-]+$/.test(l.trim()) && l.includes('-');
      if (tableLines.length >= 2) {
        for (const line of tableLines) {
          if (isSep(line)) continue;
          const cells = line.split('|').map((c: string) => c.trim()).filter((c: string) => c !== '');
          csvContent += cells.map((c: string) => `"${c.replace(/"/g, '""')}"`).join(',') + '\n';
        }
      }
    }

    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const slug = this.getQuestionForFilename(msgIndex);
    link.download = `${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  downloadPdf(messageId: string, msgIndex?: number): void {
    if (!messageId || this.downloadingPdf) {
      return;
    }
    this.downloadingPdf = messageId;
    const url = this.chatService.getPdfUrl(this.projectUuid, messageId);

    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const token = user?.token || '';

    fetch(url, {
      headers: { 'token': token },
    })
      .then(resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const slug = this.getQuestionForFilename(msgIndex);
        link.download = `${slug}-${new Date().toISOString().slice(0, 10)}.pdf`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => {
        console.error('PDF download failed', err);
      })
      .finally(() => {
        this.downloadingPdf = null;
      });
  }

  openPhoto(photo: any, photos: any[]): void {
    // Convert to Attachment-like objects for the carousel
    const attachments = (photos || [photo]).map(p => ({
      thumb_url: p.thumb_url,
      original_url: p.original_url,
      preview_url: p.original_url,
      original_name: p.caption || p.original_name || 'Photo',
      mime: 'image/jpeg',
      fileType: 'picture',
    }));

    const initialSlide = photos ? photos.indexOf(photo) : 0;

    this.dialog.open(CarouselComponent, {
      width: '90vw',
      height: '90vh',
      panelClass: 'carousel-dialog',
      data: {
        attachments,
        initialSlide: Math.max(0, initialSlide),
      },
    });
  }

  renderMarkdown(text: string): SafeHtml {
    if (!text) {
      return '';
    }

    let html = text;

    // Escape HTML entities
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Parse markdown tables BEFORE other transformations
    html = this.parseMarkdownTables(html);

    // Headers (must come before bold)
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Bold and italic
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Unordered lists
    html = html.replace(/^\- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

    // Ordered lists
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

    // Line breaks: convert double newlines to paragraphs
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');
    html = '<p>' + html + '</p>';

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[123]>)/g, '$1');
    html = html.replace(/(<\/h[123]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul>)/g, '$1');
    html = html.replace(/(<\/ul>)<\/p>/g, '$1');
    html = html.replace(/<p>(<table)/g, '$1');
    html = html.replace(/(<\/table>)<\/p>/g, '$1');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private parseMarkdownTables(text: string): string {
    // Split into lines and find consecutive runs of pipe-containing lines
    const lines = text.split('\n');
    const result: string[] = [];
    let tableLines: string[] = [];

    const isTableLine = (line: string) => {
      const trimmed = line.trim();
      // Must contain at least one pipe that's not at the very start/end only
      const pipeCount = (trimmed.match(/\|/g) || []).length;
      return pipeCount >= 2;
    };

    const isSeparator = (line: string) => /^[\s|:-]+$/.test(line.trim()) && line.includes('-');

    const flushTable = () => {
      if (tableLines.length < 2) {
        result.push(...tableLines);
        tableLines = [];
        return;
      }

      // Find header and separator
      let headerIdx = 0;
      let sepIdx = -1;
      for (let i = 1; i < tableLines.length && i < 3; i++) {
        if (isSeparator(tableLines[i])) {
          sepIdx = i;
          break;
        }
      }

      const headerLine = tableLines[headerIdx];
      const dataStartIdx = sepIdx >= 0 ? sepIdx + 1 : 1;
      const dataLines = tableLines.slice(dataStartIdx).filter(l => !isSeparator(l));

      const escapeHtml = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      const parseCells = (line: string) =>
        line.split('|').map(c => c.trim()).filter(c => c !== '');

      const headers = parseCells(headerLine);
      if (headers.length < 2) {
        result.push(...tableLines);
        tableLines = [];
        return;
      }

      const rows = dataLines.map(l => parseCells(l));

      let tableHtml = '<div class="chat-table-wrapper"><table class="chat-md-table">';
      tableHtml += '<thead><tr>';
      for (const h of headers) {
        tableHtml += `<th>${escapeHtml(h)}</th>`;
      }
      tableHtml += '</tr></thead><tbody>';
      for (const row of rows) {
        tableHtml += '<tr>';
        for (let i = 0; i < headers.length; i++) {
          tableHtml += `<td>${escapeHtml(row[i] || '')}</td>`;
        }
        tableHtml += '</tr>';
      }
      tableHtml += '</tbody></table></div>';

      result.push(tableHtml);
      tableLines = [];
    };

    for (const line of lines) {
      if (isTableLine(line)) {
        tableLines.push(line);
      } else {
        if (tableLines.length > 0) {
          flushTable();
        }
        result.push(line);
      }
    }
    // Flush any remaining table lines
    if (tableLines.length > 0) {
      flushTable();
    }

    return result.join('\n');
  }

  scrollToBottom(): void {
    try {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop =
          this.messageContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      // Ignore scroll errors
    }
  }
}
